import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Alert, AlertService, ModalComponent, ModalResult, TableConfig } from '@michelin/theme';
import { forkJoin } from 'rxjs';
import { DistributorDto } from 'src/app/shared/models/distributor/distributor-dto';
import {
  CityDto,
  CityWithAttributeDto,
  RemoveCityAttributeRequestDto,
  UpdateCityAttributeRequestDto
} from 'src/app/shared/models/location/city-dto';
import { StateDto } from 'src/app/shared/models/location/state-dto';
import { ProductLineDto } from 'src/app/shared/models/product-line/product-line-dto';
import { DistributorService } from 'src/app/shared/services/distributor/distributor.service';
import { LocationService } from 'src/app/shared/services/location/location.service';
import { ProductLineService } from 'src/app/shared/services/product-line/product-line.service';

@Component({
    selector: 'app-update-city-attribute',
    templateUrl: './update-city-attribute.component.html',
    styleUrl: './update-city-attribute.component.scss',
    standalone: false
})
export class UpdateCityAttributeComponent implements OnInit {
  @ViewChild('updateModal') updateModal: ModalComponent;
  @ViewChild('updateModalContent') updateModalContent: TemplateRef<any>;

  @ViewChild('removeModal') removeModal: ModalComponent;
  @ViewChild('removeModalContent') removeModalContent: TemplateRef<any>;

  filterForm: FormGroup;
  attributeForm: FormGroup;

  loading = false;

  states: StateDto[] = [];
  cities: CityDto[] = [];
  productLines: ProductLineDto[] = [];
  selection: CityWithAttributeDto[] = [];
  distributors: DistributorDto[] = [];

  dataSource: MatTableDataSource<CityWithAttributeDto> = new MatTableDataSource<CityWithAttributeDto>([]);

  cityTableConfig: TableConfig<CityWithAttributeDto> = {
    onSelectionChange: (tableRows: CityWithAttributeDto[]) => (this.selection = tableRows),
    columns: {
      state: { cell: (item) => `${item.stateName} (${item.stateCode})`, header: 'Estado' },
      city: { cell: (item) => item.cityName, header: 'Cidade' },
      productLine: { cell: (item) => item.productLineCode, header: 'LB' },
      priceType: { cell: (item) => item.priceType || '', header: 'Tipo de Preço' },
      distributor: { cell: 'distributorCell', header: 'Distribuidor' }
    }
  };

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly alertService: AlertService,
    private readonly locationService: LocationService,
    private readonly productLineService: ProductLineService,
    private readonly distributorService: DistributorService
  ) {
    this.filterForm = this.formBuilder.group({
      productLine: ['', Validators.required],
      state: ['', Validators.required],
      city: [''],
      priceType: [''],
      distributor: ['']
    });

    this.attributeForm = this.formBuilder.group({
      distributor: ['', Validators.required],
      priceType: ['']
    });
  }

  get productLine(): FormControl {
    return this.filterForm.get('productLine') as FormControl;
  }

  get state(): FormControl {
    return this.filterForm.get('state') as FormControl;
  }

  get city(): FormControl {
    return this.filterForm.get('city') as FormControl;
  }

  get distributor(): FormControl {
    return this.filterForm.get('distributor') as FormControl;
  }

  get attributeDistributor(): FormControl {
    return this.attributeForm.get('distributor') as FormControl;
  }

  stateDisplayName(state: StateDto): string {
    return state ? `${state.name} (${state.code})` : '';
  }

  cityDisplayName(city: CityDto): string {
    return city ? city.name : '';
  }

  productLineDisplayName(productLine: ProductLineDto): string {
    return productLine ? productLine.description : '';
  }

  distributorDisplayName(distributor: DistributorDto): string {
    return distributor ? distributor.company.tradeName : '';
  }

  getCityAttributes() {
    this.loading = true;
    this.locationService
      .findCityAttributes(this.productLine.value.id, this.state.value.id, this.city.value?.id, this.distributor.value?.id)
      .subscribe({
        next: (cityAttributes) => {
          this.dataSource.data = cityAttributes;
        },
        error: () => this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao recuperar as cidades' } as Alert)
      })
      .add(() => (this.loading = false));
  }

  markAllAsTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      }
    });
  }

  applyFilter() {
    this.markAllAsTouched(this.filterForm);
    if (this.filterForm.invalid) {
      this.alertService.showAlert({ type: 'strong', title: 'Preencha todos os campos obrigatórios (*) para realizar a busca' } as Alert);
      return;
    }
    this.getCityAttributes();
  }

  resetFilter() {
    this.filterForm.reset();
  }

  onUpdateClick() {
    this.attributeForm.reset();
    this.updateModal.open({
      id: 'updateModalContent',
      width: 600,
      title: 'Alterar Distribuidor',
      content: this.updateModalContent,
      confirm: 'Confirmar',
      cancel: 'Cancelar'
    });
  }

  onRemoveClick() {
    this.removeModal.open({
      id: 'removeModalContent',
      width: 388,
      title: 'Remover Distribuidor',
      content: this.removeModalContent,
      confirm: 'Confirmar',
      cancel: 'Cancelar'
    });
  }

  onUpdateModalClosed(event: ModalResult) {
    if (event.result) {
      if (this.attributeForm.valid) {
        const data = this.selection.map<UpdateCityAttributeRequestDto>((item) => ({
          cityId: item.cityId,
          productLineId: item.productLineId,
          distributorId: this.attributeDistributor.value.id,
          priceType: this.attributeForm.value.priceType || ''
        }));
        this.loading = true;
        this.locationService
          .updateCityAttributes(data)
          .subscribe({
            next: () => {
              this.alertService.showAlert({ type: 'success', title: 'Atributos da cidade atualizados com sucesso' } as Alert);
              this.applyFilter();
            },
            error: () => {
              this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao atualizar os atributos da cidade' } as Alert);
            }
          })
          .add(() => (this.loading = false));
      } else {
        this.alertService.showAlert({ type: 'strong', title: 'Selecione um Distribuidor para realizar a alteração' } as Alert);
      }
    }
  }

  onRemoveModalClosed(event: ModalResult) {
    if (event.result) {
      const data = this.selection.map<RemoveCityAttributeRequestDto>((item) => ({
        cityId: item.cityId,
        productLineId: item.productLineId
      }));
      this.loading = true;
      this.locationService
        .removeCityAttributes(data)
        .subscribe({
          next: () => {
            this.alertService.showAlert({ type: 'success', title: 'Distribuidores removidos com sucesso' } as Alert);
            this.applyFilter();
          },
          error: () => {
            this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao remover os distribuidores' } as Alert);
          }
        })
        .add(() => (this.loading = false));
    }
  }

  ngOnInit() {
    this.loading = true;

    this.city.disable();

    forkJoin({
      states: this.locationService.findAllStates(),
      productLines: this.productLineService.findAll(),
      distributors: this.distributorService.findAll()
    })
      .subscribe({
        next: (result) => {
          this.states = result.states;
          this.productLines = result.productLines;
          this.distributors = result.distributors;
        },
        error: () => {
          this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao recuperar as informações' } as Alert);
        }
      })
      .add(() => (this.loading = false));

    this.state.valueChanges.subscribe((state: StateDto) => {
      if (state && state.id) {
        this.loading = true;
        this.city.reset();
        this.locationService
          .findCitiesByState(state.id)
          .subscribe({
            next: (cities) => {
              this.cities = cities;
              this.city.enable();
            },
            error: () => {
              this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao realizar a busca' } as Alert);
              this.city.disable();
              this.cities = [];
            }
          })
          .add(() => (this.loading = false));
      } else {
        this.city.disable();
        this.cities = [];
      }
    });
  }
}
